import { Box, Stack } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import PathListEmptyState from "features/Path/Components/PathsList/PathListEmptyState";
import PathRow from "features/Path/Components/PathsList/PathRow";
import PathsToolbar, {
  AssignmentFilter,
  SortDirection,
} from "features/Path/Components/PathsList/PathsToolbar";
import { usePathBatchCountsQuery, useV2PathListQuery } from "features/Path/hooks";
import { Path, PathDetails } from "features/Path/models";

function matchesAssignment(path: Path, filter: AssignmentFilter): boolean {
  if (filter === "all") return true;
  if (filter === "auto") return path.autoAdd;
  return !path.autoAdd;
}

function matchesSearch(path: Path, search: string): boolean {
  if (!search) return true;
  return path.name.toLowerCase().includes(search.toLowerCase());
}

// Unassigned paths (null lastUsedAt) always sink to the bottom, regardless of sort direction.
function sortByLastUsed(paths: Path[], detailsMap: Record<string, PathDetails>, direction: SortDirection): Path[] {
  return [...paths].sort((a, b) => {
    const aTime = detailsMap[a.id]?.lastUsedAt ? new Date(detailsMap[a.id].lastUsedAt!).getTime() : null;
    const bTime = detailsMap[b.id]?.lastUsedAt ? new Date(detailsMap[b.id].lastUsedAt!).getTime() : null;
    if (!aTime && !bTime) return 0;
    if (!aTime) return 1;
    if (!bTime) return -1;
    return direction === "desc" ? bTime - aTime : aTime - bTime;
  });
}

export default function PathsListView() {
  const [search, setSearch] = useState("");
  const [assignmentFilter, setAssignmentFilter] = useState<AssignmentFilter>("all");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const { data, isLoading } = useV2PathListQuery("");

  const pathIds = useMemo(() => (data ?? []).map((p) => p.id), [data]);
  const { data: detailsMap = {}, isLoading: isDetailsLoading } = usePathBatchCountsQuery(pathIds);

  const visible = useMemo(() => {
    const paths = data ?? [];
    const filtered = paths.filter(
      (p) => matchesAssignment(p, assignmentFilter) && matchesSearch(p, search),
    );
    // Don't sort by lastUsedAt until details have loaded — avoids a visible
    // re-order when the batch-counts response arrives.
    if (isDetailsLoading) return filtered;
    return sortByLastUsed(filtered, detailsMap, sortDirection);
  }, [data, detailsMap, isDetailsLoading, assignmentFilter, search, sortDirection]);

  return (
    <Stack direction="column" gap="5" w="100%">
      <PathsToolbar
        search={search}
        onSearchChange={setSearch}
        assignmentFilter={assignmentFilter}
        onAssignmentFilterChange={setAssignmentFilter}
        sortDirection={sortDirection}
        onSortDirectionToggle={() => setSortDirection((d) => (d === "desc" ? "asc" : "desc"))}
      />

      {!isLoading && visible.length === 0 ? (
        <PathListEmptyState />
      ) : (
        <Box w="100%" borderTopWidth="1px" borderColor="gray.100">
          {visible.map((path) => (
            <PathRow
              key={path.id}
              path={path}
              details={detailsMap[path.id]}
              isDetailsLoading={isDetailsLoading}
            />
          ))}
        </Box>
      )}
    </Stack>
  );
}
